import { useCallback, useEffect, useRef, useState } from 'react'
import { REPOSITORY } from '../../../api/repository/Repository'
import { useLoadingContext } from '../Context/LoadingContext'

const LIMIT = 20
const arrayToObject = (array, _key) => {
  return array.reduce((acc, _element) => {
    return {
      ...acc,
      [_element[_key]]: _element,
    }
  }, {})
}

const arraysToMerge = (array1, array2, key) => {
  return [arrayToObject(array1, key), arrayToObject(array2, key)]
}

const mergeObjects = (previous, newObject, newData) => {
  return Object.keys(previous).reduce((acc, item) => {
    const foundKey = newObject[item]

    let newValue = {}

    if (foundKey) {
      newValue = { ...previous[item], ...foundKey, ...newData }
    } else {
      newValue = previous[item]
    }

    return [...acc, newValue]
  }, [])
}

export const useFoundEmployee = (
  {
    sEstado = [],
    sBusqueda = '',
    sNombre = '',
    sOrderby = '',
    bAsc = true,
    bReporte = false,
  },
  getAll = false,
) => {
  const [employees, setEmployees] = useState([])
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(null)
  const timeout = useRef()
  const [actualFilters, setActualFilters] = useState({
    sRut: sBusqueda.trim(),
    sNombre: sNombre.trim(),
    sEstado: sEstado?.join(','),
    bReporte: bReporte,
  })
  const handleChangeFilter = useCallback((key, value) => {
    setActualFilters((prevState) => ({ ...prevState, [key]: value }))
  }, [])
  useEffect(() => {
    setActualFilters((prevState) => {
      return {
        ...prevState,
        sRut: sBusqueda.trim(),
        sNombre: sNombre.trim(),
      }
    })
  }, [sNombre, sBusqueda])
  const { sEmpresa } = REPOSITORY.local.company.getUserData()
  const { handleOpen: handleOpenLoading, handleClose: handleCLoseLoading } =
    useLoadingContext()
  const [allEmployees, setAllEmployees] = useState([])
  const [showExport, setShowExport] = useState(false)

  const handleEmployees = useCallback(
    async (newFilters) => {
      handleOpenLoading()
      setShowExport(false)
      const filterBy = newFilters
        ? {
            page: 1,
            limit: LIMIT,
            enterprice: sEmpresa,
            ...newFilters,
            sEstado: newFilters?.sEstado,
            bAsc: true,
            bOmitirPaginacion: getAll,
          }
        : {
            page: 1,
            limit: LIMIT,
            enterprice: sEmpresa,
            ...actualFilters,
            sEstado: actualFilters?.sEstado,
            bAsc: true,
            bOmitirPaginacion: getAll,
          }
      await REPOSITORY.wsEmpresa.employees
        .getEmployees({
          ...filterBy,
          bDiscount: getAll,
        })
        .then((res) => {
          setAllEmployees(res.data.cDatosEmpleados)

          handleCLoseLoading()
        })
        .catch((error) => {
          console.error('error', error)

          handleCLoseLoading()
        })
      setShowExport(true)
    },
    [actualFilters, sEmpresa],
  )
  const updateStates = useCallback((response) => {
    setEmployees(response.data.cDatosEmpleados)
    setTotalPages(response.data.nPaginasTotal)
    setPage(response.data.nPagina)
  }, [])

  const applyFilters = async (filters) => {
    handleOpenLoading()
    const newFilters = filters.sEstado
      ? {
          ...filters,
          sEstado: filters?.sEstado?.join(',') || '',
        }
      : { ...filters }
    setActualFilters((prevState) => ({ ...prevState, ...newFilters }))
    clearTimeout(timeout.current)
    timeout.current = setTimeout(async () => {
      if (getAll) {
        await handleEmployees(newFilters)
        setShowExport(true)
      }
      await REPOSITORY.wsEmpresa.employees
        .getEmployees({
          page: 1,
          limit: LIMIT,
          enterprice: sEmpresa,
          ...newFilters,
          sOrderby,
          bAsc,
          bOmitirPaginacion: getAll,
          bDiscount: getAll,
        })
        .then(updateStates)
        .catch((error) => console.error('error', error))
      if (!getAll) {
        handleCLoseLoading()
      }
    }, 200)
    setShowExport(true)
  }

  const fetchEmployees = useCallback(
    async (searchPage) => {
      let filters = {}
      setActualFilters((prevState) => {
        filters = { ...prevState }
        return { ...prevState }
      })
      clearTimeout(timeout.current)
      timeout.current = setTimeout(() => {
        const body = {
          page: searchPage,
          limit: LIMIT,
          enterprice: sEmpresa,
          ...filters,
          bOmitirPaginacion: getAll,
        }
        REPOSITORY.wsEmpresa.employees
          .getEmployees({
            ...body,
            bDiscount: getAll,
          })
          .then(updateStates)
          .catch((error) => console.error('error', error))
        if (!getAll) {
          handleCLoseLoading()
        }
      }, 500)
    },
    [actualFilters, getAll, sEmpresa, LIMIT, updateStates],
  )

  const updateEmployees = useCallback((newEmployees) => {
    setEmployees((prevState) => {
      const [prevObject, newData] = arraysToMerge(
        prevState,
        newEmployees.cDatosEmpleados,
        'sEmpleado',
      )
      return mergeObjects(prevObject, newData, {
        sEstado: newEmployees.sEstado,
      })
    })
    if (getAll) {
      setAllEmployees((prevState) => {
        const [prevObject, newData] = arraysToMerge(
          prevState,
          newEmployees.cDatosEmpleados,
          'sEmpleado',
        )
        return mergeObjects(prevObject, newData, {
          sEstado: newEmployees.sEstado,
        })
      })
    }
  }, [])

  const handleChangeEmployee = useCallback((newEmployees) => {
    setEmployees(newEmployees)
    if (getAll) {
      setAllEmployees(newEmployees)
    }
  }, [])
  const resetEmployees = (newEmployees) => {
    setEmployees(newEmployees)
    if (getAll) {
      setAllEmployees(newEmployees)
    }
  }
  useEffect(() => {}, [actualFilters])

  const handleChangePage = async (newPage) => {
    handleOpenLoading()
    await fetchEmployees(newPage)
    if (getAll) {
      await handleEmployees()
    }
  }
  useEffect(() => {
    handleChangePage(1)
  }, [])

  // useEffect(() => {
  //   reFetch()
  //   handleCLoseLoading()
  // }, [sOrderby, bAsc])

  return {
    totalPages,
    employees,
    page,
    actualFilters,
    allEmployees,
    showExport,
    handleChangeFilter,
    handleChangePage,
    updateEmployees,
    resetEmployees,
    applyFilters,
    handleChangeEmployee,
  }
}
